<template>
  <div class="container">
    <div v-if="loading || ticketsListLoading">
      <spinner />
    </div>
    <event-password-login
      v-else-if="event.flags && event.flags.eventPasswords && hasValidPasswords && !eventPasswordAccepted"
      :event-id="event.id"
      @acceptEventPassword="eventPasswordAccepted = true"
    />
    <div v-else>
      <link v-if="customStyles.stylesheet" rel="stylesheet" :href="customStyles.stylesheet">
      <div v-if="event.applicationsOpen">
        <div
          v-if="!showMessage"
          class="grid-container"
          :style="
            event.extraModules.ticketing ? 'justify-content:flex-end' : 'justify-content:center'
          "
        >
          <div v-if="!isZkm && event.cover" class="card-header-with-cover">
            <div class="event-cover">
              <img :src="event.cover" alt="cover image">
            </div>
            <card-header-cover
              v-if="(!isZkm && event.cover) || activeStep !== 1"
              :class="['overflow-card', activeStep !== 1 && 'overflow-card-step-two']"
              :event="event"
              :active-step="activeStep"
            />
            <div
              v-if="showPromoCodeInputInRegisterContainer(Number(event.id), event.organization.id) && (!isGoogleLoginDisabled || useEmailLogin)"
              :style="activeStep !== 1 && 'display:none'"
              :class="['promo-code-zkm', 'promo-code-container']"
            >
              <add-promo-code
                ref="addPromoCodeRef"
                :promo-code="promoCode"
                :membership="membership"
                :event="event"
                :initial-promo-code="initialPromoCode"
                :is-zkm="isZkm"
                @applySubscription="applySubscription"
                @removeSubscription="removeSubscription"
                @applyPromoCode="applyPromoCode"
                @applyMembership="handleMembershipClick"
                @removePromoCode="removePromoCodeAndMembership"
              />
            </div>
          </div>
          <v-flex v-else>
            <card-header
              ref="cardHeaderRef"
              :style="activeStep !== 1 && 'display:none'"
              :event="event"
              :membership="membership"
              :initial-promo-code="initialPromoCode"
              :promo-code="promoCode"
              :initial-values="shoppingCart"
              :active-step="activeStep"
              :hide-add-promo-code="isGoogleLoginDisabled && !useEmailLogin"
              @applyPromo="applyPromoCode"
              @removePromoCode="removePromoCode"
              @applyMembership="applyMembership"
              @applySubscription="applySubscription"
              @removeSubscription="removeSubscription"
              @applyMembershipInParent="handleMembershipClick"
              @removeMembership="handleRemoveMembershipZkm"
            />
          </v-flex>
          <main
            :class="[
              activeStep !== 1 && 'main-row',
              !event.cover && (isZkm ? 'main-no-image-zkm' : 'main-no-image'),
            ]"
          >
            <div
              :class="['ticket-list-container', event.cover && 'ticket-list-container-with-cover']"
            >
              <div v-if="activeStep === 1 && event.extraModules.ticketing">
                <div v-if="isGoogleLoginDisabled && !useEmailLogin" class="google-login-disabled-container">
                  <div class="open-in-default-browser-container">
                    <img src="/assets/google-login.webp" style="height: 2.5rem; width: 2.5rem" alt="google logo">
                    <p v-if="isGoogleLoginDisabled" class="external-browser-text">
                      {{ $t('openInExternalBrowser') }}<span
                        style="font-weight: 700"
                      >{{ $t('openInExternalBrowserBold') }}</span>
                    </p>
                    <main-button
                      outline
                      color="grey_00"
                      style="border-color: rgba(151, 151, 151, 0.50)"
                      @click="redirectToDefaultBrowser"
                    >
                      {{ $t('openInBrowser') }}
                    </main-button>
                  </div>
                  <div class="email-login-divider">
                    <div class="spacer" />
                    <div class="separator-text">{{ $t('loginWithEmail') }}</div>
                    <div class="spacer" />
                  </div>
                  <main-button outline color="grey_00" class="continue-here-btn" @click="useEmailLogin = true">
                    {{ $t('continueHere') }}
                    <v-icon>arrow_forward</v-icon>
                  </main-button>
                </div>
                <tickets-list
                  v-else-if="!event.extraModules.floorPlan"
                  ref="childList"
                  :event="event"
                  :initial-promo-code="initialPromoCode"
                  :color="getThemeStyle(event.coverImagePrimaryColor, null, 'primary')"
                  :initial-values="shoppingCart"
                  :show-header="Number(event.id) !== 2532"
                  :membership="membership"
                  :promo-code="promoCode"
                  :num-of-remaining-tickets="numOfRemainingTickets"
                  @back="
                    form.ticket = $event
                    goBack()
                  "
                  @next="onTicketsSelected($event)"
                  @selectedTickets="selectedTickets"
                  @applyPromo="applyPromoCode"
                  @applyMembership="applyMembership"
                  @removePromoCode="removePromoCode"
                  @startLoading="ticketsListLoading = true"
                  @finishLoading="ticketsListLoading = false"
                />

                <floor-plan
                  v-else
                  ref="childFloor"
                  :event="event"
                  :initial-promo-code="initialPromoCode"
                  :initial-values="shoppingCart"
                  :ticket-locations="ticketLocations"
                  :initial-seat-select="initialSeatSelect"
                  :promo-code="promoCode"
                  :membership="membership"
                  @applyPromo="applyPromoCode"
                  @applyMembership="applyMembership"
                  @applySubscription="applySubscription"
                  @removePromoCode="removePromoCode"
                  @addSelectedTickets="addSelectedSeat"
                  @removeSelectedTickets="removeSelectedSeat"
                  @removeSelectedTableTickets="removeSelectedTableTickets"
                  @addDialogElementToOrder="addDialogElementToOrder"
                  @back="
                    form.ticket = $event
                    goBack()
                  "
                  @next="onFloorPlanSeatSelect($event)"
                />
              </div>
              <attendee-data
                v-else-if="activeStep === 1"
                :event="event"
                :schema="schema"
                :styles="customStyles.attendeeForm"
                :initial-values="form.attendee"
                @back="
                  form.attendee = $event
                  goBack()
                "
                @next="next($event)"
                @submit="
                  form.attendee = $event
                  submitStepper()
                "
              />
              <attendee-data
                v-if="activeStep === 2 && !event.extraModules.ticketing && customAttendeeFields"
                :event="event"
                :schema="schema"
                :styles="customStyles.attendeeForm"
                :initial-values="form.attendee"
                @back="
                  form.attendee = $event
                  goBack()
                "
                @next="
                  form.attendee = $event
                  purchaseTicket('CreditCard')
                "
                @submit="
                  form.attendee = $event
                  purchaseTicket('CreditCard')
                "
              />
              <checkout-timer
                v-if="
                  event.extraModules.ticketing && shoppingCart.length && activeStep !== 1 && !isZkm
                "
                :start-time="startTime"
                :event="event"
                :class="[
                  !event.cover
                    ? 'checkout-timer-regular-mobile-no-cover'
                    : 'checkout-timer-regular-mobile',
                ]"
              />
              <checkout
                v-if="activeStep === 2 && event.extraModules.ticketing"
                ref="checkoutRef"
                :event="event"
                :schema="schema"
                :styles="customStyles.attendeeForm"
                :initial-values="form.attendee"
                :selected-tickets="shoppingCart"
                :total-order-price="order ? order.price : totalPrice()"
                :is-google-login-disabled="isGoogleLoginDisabled"
                @user="user = $event"
                @back="clearTicketCart()"
                @next="
                  const { paymentType, ...attendeeData } = $event
                  form.attendee = attendeeData
                  purchaseTicket(paymentType)
                "
                @applyGiftCard="applyGiftCard"
              />
              <user-message-dialog
                :show.sync="showUserMessageDialog"
                :user-message-dialog="userMessageDialog"
              />
              <!--
              <keks-payment
                v-if="activeStep === 3"
                :order="order"
                :event="event"
                @back="activeStep = 2"
              />
              -->
              <workshops-list
                v-if="activeStep === 4"
                ref="workshopsList"
                :event="event"
                :selected="form.workshops"
                :workshops="event.workshops"
                :attendee="form.attendee"
                :styles="customStyles.workshopsForm"
                @back="
                  form.workshops = $event
                  activeStep = event.extraModules.ticketing ? 2 : 1
                "
                @submit="
                  form.workshops = $event
                  event.extraModules.ticketing ? purchaseTicket('') : submitStepper()
                "
              />
            </div>
          </main>
          <aside
            v-if="event.extraModules.ticketing"
            :class="['cover-header-style', isZkm && 'cover-header-style-zkm']"
          >
            <checkout-timer
              v-if="
                event.extraModules.ticketing && shoppingCart.length && activeStep !== 1 && !isZkm
              "
              :start-time="startTime"
              :event="event"
              class="checkout-timer-regular"
            />
            <ticket-price-list
              v-if="event.extraModules.ticketing && shoppingCart.length !== 0"
              ref="ticketPriceList"
              :key="subscription.length"
              :class="[isZkm && shoppingCart.length === 0 && 'cover-header-style-zkm-border']"
              :event="event"
              :membership="membership"
              :start-time="startTime"
              :subscription="subscription"
              :selected-tickets="shoppingCart"
              :cover="event.cover"
              :promo-code="promoCode"
              :ticket-locations="ticketLocations"
              :shopping-cart="shoppingCart"
              :active-step="activeStep"
              :user="user"
              :gift-card="giftCard"
              @back="clearTicketCart()"
              @triggerClick="handleClickInChildA()"
              @triggerPayment="handlePaymentClick()"
            />
          </aside>
        </div>
        <v-layout v-else justify-center>
          <v-flex
            shrink
            class="registration-success-page"
            :style="Number(event.id) === 2849 && 'margin: 0 !important'"
          >
            <img
              v-if="Number(event.id) === 2849"
              class="info-image"
              src="../../../../public/assets/a1-since-1999-end.jpg"
              alt="mail_successful"
            >
            <img
              v-else
              class="info-image"
              src="../../../../public/assets/mail_success.svg"
              alt="mail_successful"
            >
            <div class="my-page-title">
              <span v-if="customStyles.base && customStyles.base.submitNote">{{
                customStyles.base.submitNote
              }}</span>
              <span v-else>{{ $t('registrationFinish') }}</span>
            </div>
            <div v-if="customStyles.base && customStyles.base.submitInfo" class="description">
              {{ customStyles.base.submitInfo }}
            </div>
            <div v-else class="description">
              {{ Number(event.id) === 3710 ? '' : $t('registrationDescription') }}
            </div>
            <div v-if="![2849, 3148, 3710].includes(Number(event.id))" class="mt-12 text-center">
              <main-button
                :text="(customStyles.base && customStyles.base.addAnother) || $t('addAttendee')"
                @click="addAnother"
              />
            </div>
          </v-flex>
        </v-layout>
        <simple-snackbar :open.sync="showError" color="primary" :message-prop="snackbarMessage" />
      </div>
      <v-flex v-else xs12>
        <v-layout wrap justify-center style="height: 100%">
          <v-flex shrink class="registration-success-page">
            <img src="/assets/dont-know-ticket.svg" alt="no tickets">
            <h1 v-if="customStyles.base && customStyles.base.eventClosed" class="my-page-title">
              {{ customStyles.base.eventClosed }}
            </h1>
            <module-title
              v-else
              color="#333333"
              :title="$t('ticketUnavailable')"
              :description="$t('ticketUnavailableDescription')"
              :show-events-button="true"
            />
          </v-flex>
        </v-layout>
      </v-flex>
      <simple-snackbar
        :open.sync="snackbarOpen"
        :message-prop="errorMessage"
        :timeout="Number(event.id) === 2444 ? 15000 : 4000"
        color="error"
      />
    </div>
  </div>
</template>

<script>
import {
  CHECK_IF_ATTENDEE_ALREADY_EXISTS,
  GET_EVENT_DETAILS_FOR_REGISTER_CONTAINER,
  GET_WORKSHOP_DETAILS_BY_EVENT_SLUG,
} from '@/graphql/event.graphql'
import CardHeader from '../../shared/card-header.vue'
import Spinner from './../../shared/spinner.vue'
import AttendeeData from '../register-stepper/attendee-data.vue'
import WorkshopsList from '../register-stepper/workshops-list.vue'
import TicketsList from '../register-stepper/tickets-list.vue'
import CheckoutTimer from '../register-stepper/checkout-timer.vue'
import {
  createFloorPlanPurchaseAccreditationsPublic,
  createPendingAccreditationPublic,
  createPurchaseAccreditationsPublic,
  fetchSelectedSeatsByOrderUUID,
  getOrderInfoByUUID,
  populateOrderData,
} from '@/http'
import SimpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import {
  createAttendeeSchema,
  createPurchaseSubmitFormWSPay, getIOSVersion,
  initializeRegisterForm, isInAppBrowser,
  showPromoCodeInputInRegisterContainer,
  submitGuard,
} from '@/utils'
import CardHeaderCover from '../../shared/card-header-cover.vue'
import TicketPriceList from '../register-stepper/ticket-price-list.vue'
import Checkout from '../register-stepper/checkout.vue'
import FloorPlan from '../register-stepper/floor-plan.vue'
import moment from 'moment'
import i18next from 'i18next'
import AddPromoCode from '@/components/tickets/add-promo-code.vue'
import { TicketType } from '@/types'
import ModuleTitle from '@/components/shared/module-title.vue'
import EventPasswordLogin from '@/components/accreditations/event-password-login.vue'
import UserMessageDialog from '@/components/accreditations/register-stepper/user-message-dialog.vue'

export default {
  name: 'register',
  components: {
    UserMessageDialog,
    EventPasswordLogin,
    ModuleTitle,
    AddPromoCode,
    Spinner,
    WorkshopsList,
    CardHeader,
    AttendeeData,
    SimpleSnackbar,
    TicketsList,
    CardHeaderCover,
    TicketPriceList,
    Checkout,
    FloorPlan,
    CheckoutTimer,
  },
  props: {
    eventSlug: {
      type: String,
      default: null,
    },
    status: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    showMessage: false,
    activeStep: 1,
    event: null,
    form: {
      attendee: {},
      workshops: [],
    },
    showError: false,
    initialSeatSelect: null,
    customStyles: {},
    schema: {},
    userMessageDialog: {},
    initialPromoCode: null,
    snackbarOpen: false,
    showUserMessageDialog: false,
    errorMessage: '',
    customAttendeeFields: false,
    snackbarMessage: '',
    ticketLocations: {},
    initialAttendee: {},
    loading: true,
    coverColor: {},
    backgroundCoverColor: {},
    shoppingCart: [],
    promoCode: null,
    membership: null,
    subscription: [],
    order: null,
    startTime: null,
    numOfRemainingTickets: 0,
    removeSeat: [],
    user: null,
    giftCard: null,
    ticketsListLoading: false,
    eventPasswordAccepted: false,
    isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
    useEmailLogin: false,
    hasValidPasswords: false,
  }),

  computed: {
    referralName() {
      return this.$route.query.ref
    },
    isZkm() {
      return this.event.organization.id == process.env.VUE_APP_ZKM_ORGANIZATION_ID
    },
    isGoogleLoginDisabled() {
      return this.isMobile && isInAppBrowser(navigator.userAgent)
    },
  },
  mounted() {
    if(this.eventSlug === 'buc-kesidi-u-vintage-u-bc69'){
      this.loading = false
      this.ticketLocations = false
      this.event = { applicationsOpen: false }
    }
    // remove this redirect when event is over
    if (this.eventSlug === 'maci-gray-u-tvornici-kulture-f097') {
      window.location.replace(
        'https://app.core-event.co/events/macy-gray-u-tvornici-kulture-f097/register',
      )
    }
    if (this.eventSlug === 'krule-u-zagrebu-novi-termin-70f3') {
      window.location.replace(
        'https://app.core-event.co/events/stand-up-bloger-krule-kino-sc-zagreb-70f3/register',
      )
    }
  },
  apollo: {
    getEventDetailsForRegisterContainer: {
      query: GET_EVENT_DETAILS_FOR_REGISTER_CONTAINER,
      variables() {
        return {
          slug: this.eventSlug,
        }
      },
      manual: true,
      async result({ loading, data }) {
        if (!loading) {
          const { getEventDetailsForRegisterContainer: event } = data
          const eventTemplate = event.eventTemplate
          this.event = { ...event, ...eventTemplate }
          this.customAttendeeFields = event.flags && event.flags.customAttendeeFields
          this.numOfRemainingTickets = event.numOfRemainingTickets

          if (this.event.translation) {
            for (const translation in this.event.translation.translationsJSON) {
              i18next.addResourceBundle(
                translation,
                'default',
                this.event.translation.translationsJSON[translation],
                true,
                false,
              )
            }
          }
          this.customStyles =
            (event.eventUtils &&
              event.eventUtils.inviteFormStyles &&
              event.eventUtils.inviteFormStyles) ||
            {}
          this.schema = createAttendeeSchema(event.attendeeFields)

          this.initialPromoCode = this.$route.query.promo
          const item = await this.parseSavedOrder()

          if (item && item[0]) {
            const [savedOrder, startedAt] = item

            this.order = savedOrder
            this.activeStep = 2

            const form = await initializeRegisterForm(savedOrder)
            this.form = form[0]
            this.shoppingCart = form[1]
            this.startTime = new Date(startedAt)
            const purchaseWithPromoCode = this.order.purchases.find(p => p.promoCode)
            if (purchaseWithPromoCode) {
              this.initialPromoCode = purchaseWithPromoCode.promoCode.code
            }
          }
          if (this.event.venueId && this.event.extraModules.floorPlan && this.order) {
            this.initialSeatSelect = await fetchSelectedSeatsByOrderUUID(this.order.uuid)
            this.selectedSeat([this.shoppingCart, [...this.initialSeatSelect.purchases]])
          }
          if (this.event.extraModules.workshops) {
            await this.getWorkshopDetails()
          }

          // Check event passwords expiration
          if (this.event.eventPasswords && this.event.eventPasswords.length) {
            const now = new Date()
            this.hasValidPasswords = this.event.eventPasswords.some(password => {
              if (!password.expiresAt) {
                return true // If no expiration date, consider it valid
              }

              return new Date(password.expiresAt) > now
            })
          } else {
            this.hasValidPasswords = false
          }

          this.loading = false
        }
      },
    },
  },
  methods: {
    showPromoCodeInputInRegisterContainer,
    handlePaymentClick() {
      this.$refs.checkoutRef.triggerButtonClick()
    },
    handleMembershipClick(membershipNumber) {
      if (this.event.extraModules.floorPlan) {
        this.$refs.childFloor.triggerMembershipClick(membershipNumber)
      } else {
        this.$refs.childList.triggerMembershipClick(membershipNumber)
      }
    },
    handleRemoveMembershipZkm() {
      if (this.event.extraModules.floorPlan) {
        this.$refs.childFloor.removePromoCode()
      }
      this.shoppingCart = this.shoppingCart.filter(t => !t.membership)
      this.membership = null
    },
    handleClickInChildA() {
      if (this.$refs.childFloor) {
        this.$refs.childFloor.triggerButtonClick()
      } else if (this.$refs.childList) {
        this.isZkm ? this.$refs.childList.triggerButtonClick() : this.onTicketsSelected()
      }
    },
    async clearTicketCart() {
      if (!this.event.venueId) {
        this.subscription = []
        if (!this.isZkm && this.event.cover) {
          this.$refs.addPromoCodeRef.removePromoCode()
        } else {
          this.$refs.cardHeaderRef.$refs.addPromoCodeRef.removePromoCode()
        }
        this.shoppingCart = []
        this.membership = null
        this.promoCode = null
        this.order = null
        localStorage.removeItem('order')
        this.activeStep = 1
      } else {
        this.initialSeatSelect = await fetchSelectedSeatsByOrderUUID(this.order.uuid)
        this.activeStep = 1
      }
      this.giftCard = null
    },
    async parseSavedOrder() {
      const savedOrder = localStorage.getItem('order')
      if (!savedOrder) {
        return null
      }
      const order = JSON.parse(savedOrder)
      const { orderUUID, startedAt, eventId } = order
      if(eventId !== this.event.id){
        return null
      }
      const differenceInMinutes = moment(new Date()).diff(moment(startedAt), 'minutes')
      if (differenceInMinutes >= 15) {
        localStorage.removeItem('order')

        return null
      }
      const found = await getOrderInfoByUUID(orderUUID)

      return [found, startedAt]
    },
    async onTicketsSelected() {
      //TODO REMOVE MEMBERSHIP IF NO MEMBERSHIP TICKETS ARE SELECTED
      const variables = {
        eventId: this.event.id,
        attendees: this.shoppingCart.map(s => ({ fields: { ticketId: s.id } })),
        workshopIds: [],
        referralName: this.referralName,
        promoCodeId: this.promoCode ? this.promoCode.id : null,
        membershipUserCode: this.membership ? this.membership.code : null,
        subscriptions: this.subscription,
        buyer: { fields: {} },
        price: this.totalPrice(),
      }
      const response = await createPurchaseAccreditationsPublic(variables)
      if (response && !response.errors) {
        const {
          data: {
            createPurchaseAccreditationsPublic: {
              createdOrder: { uuid: orderUUID },
            },
          },
        } = response
        localStorage.setItem(
          'order',
          JSON.stringify({
            orderUUID,
            startedAt: new Date(),
            eventId: this.event.id,
          }),
        )
        this.startTime = new Date()
        this.activeStep = 2
        this.order = await getOrderInfoByUUID(orderUUID)
      } else {
        this.showError = true
        this.snackbarMessage = this.event.eventTemplateId
          ? response.errors[0].message
          : 'Promijenite narudžbu. Karte su već rezervirane.'
      }
    },
    async onFloorPlanSeatSelect(selectedSeats) {
      const localStorageOrder = this.parseSavedOrder()
      const attendeePurchasePublicData = {
        attendees: this.shoppingCart.map(s => ({ fields: { ticketId: s.id } })),
        selectedSeats: selectedSeats,
        referralName: this.referralName,
        membershipUserCode: this.membership ? this.membership.code : null,
        subscriptions: this.subscription,
        orderUUID: localStorageOrder && localStorageOrder.orderUUID,
        promoCode: this.promoCode ? this.promoCode.id : null,
        buyer: { fields: {} },
        price: this.totalPrice(),
      }
      const response = await createFloorPlanPurchaseAccreditationsPublic({
        eventId: this.event.id,
        attendeePurchasePublicData,
      })

      if (response && !response.errors) {
        const {
          data: {
            createFloorPlanPurchaseAccreditationsPublic: {
              createdOrder: { uuid: orderUUID },
            },
          },
        } = response
        localStorage.setItem(
          'order',
          JSON.stringify({
            orderUUID,
            startedAt: new Date(),
            eventId: this.event.id,
          }),
        )
        this.startTime = new Date()
        this.activeStep = 2
        this.order = await getOrderInfoByUUID(orderUUID)

        return
      } else {
        this.showError = true
        this.snackbarMessage =
          response.errors[0].message || 'Promijenite narudžbu. Sjedala su već rezervirana.'
      }
    },
    async createAttendee({ attendee, workshops }, skipReset = false) {
      const { category: categoryId, ...fields } = attendee
      const variables = {
        eventId: this.event.id,
        attendee: {
          fields,
          categoryId,
        },
        workshopIds: workshops,
        referralName: this.referralName,
        ticketingEnabled: false,
      }
      const response = await createPendingAccreditationPublic(variables)
      if (skipReset) return response

      if (response && !response.errors) {
        this.showMessage = true
      } else {
        this.form.workshops = []
        this.showError = true
        this.snackbarMessage = response.errors[0].message
      }
      await this.$apollo.queries.getEventDetailsForRegisterContainer.refetch()
    },
    async submitStepper() {
      await this.createAttendee(this.form)
    },
    async next(data) {
      this.form.attendee = data

      const checkIfAttendeeAlreadyExists =
        [2444, 2341].includes(Number(this.event.id)) && (await this.checkAttendeeExists())
      if (checkIfAttendeeAlreadyExists) {
        this.errorMessage =
          'Sudionik s ovom adresom e-pošte već se prijavio za događaj. Vaš potvrdni mail prijave ponovno Vam je poslan. Molimo provjerite inbox Vaše e-pošte.'
        this.snackbarOpen = true

        return
      }

      this.activeStep = this.event.extraModules.workshops ? 4 : this.submitStepper()
    },
    async checkAttendeeExists() {
      const { data } = await this.$apollo.query({
        query: CHECK_IF_ATTENDEE_ALREADY_EXISTS,
        variables: {
          eventId: this.event.id,
          attendee: {
            email: this.form.attendee.Email,
            firstName: this.form.attendee['First name'].trim(),
            lastName: this.form.attendee['Last name'].trim(),
          },
        },
      })

      return data.checkIfAttendeeAlreadyExistsOnEvent
    },
    async getWorkshopDetails() {
      const { data } = await this.$apollo.query({
        query: GET_WORKSHOP_DETAILS_BY_EVENT_SLUG,
        variables: {
          slug: this.eventSlug,
        },
      })

      return (this.event = { ...this.event, ...data.getWorkshopDetailsByEventSlug })
    },
    getLanguageCodeFromQueryParams() {
      const params = new URL(window.location.href).searchParams

      return params.get('lang')
    },
    async purchaseTicket(paymentType) {
      this.$refs.checkoutRef.$data.disabledSubmitButton = true
      this.$refs.ticketPriceList.$data.disabledSubmitButton = true

      const timeDiff = moment.duration(moment().diff(moment(this.startTime)))
      if (timeDiff.asMinutes() >= 15) {
        localStorage.removeItem('order')
        window.location.reload()
      }
      const {
        attendee: { tickets, ...rest },
      } = this.form
      rest.getOrganizerSubscription = rest.getSubscription || false
      const { purchases } = this.order
      let legalEntity = null
      if (rest.r1Invoice) {
        legalEntity = {
          name: rest.legalEntityName,
          OIB: rest.legalEntityOIB,
          address: rest.legalEntityAddress,
          zipCode: rest.legalEntityZipcode,
          city: rest.legalEntityCity,
          country: rest.legalEntityCountry,
          contactName: '',
          email: '',
          phone: '',
        }
      }
      const {
        'First name': firstName,
        'Last name': lastName,
        Email: email,
        ...remainingFields
      } = rest
      const variables = {
        eventId: this.event.id,
        orderId: this.order.id,
        buyer: {
          fields: {
            firstName,
            lastName,
            email,
            ...remainingFields,
          },
        },
        attendees: purchases.map(p => ({
          fields: {
            'First name': firstName,
            'Last name': lastName,
            Email: email,
            ...remainingFields,
            //custom fields for event.id = 2445 - delete when event is over
            ...(Number(this.event.id) === 2445
              ? { Function: rest.function, 'Company/organization name': rest.companyOrganization }
              : {}),
          },
          id: p.attendeeId,
        })),
        legalEntity,
        lang: this.getLanguageCodeFromQueryParams(),
      }

      const { data, errors } = await populateOrderData(variables)
      if (!data) {
        this.$refs.checkoutRef.$data.disabledSubmitButton = false
        this.$refs.ticketPriceList.$data.disabledSubmitButton = false

        if (errors) {
          //custom feat for futsal dinamo
          const notMember =
            errors[0].message ===
            'Ticket sales are currently available only to users with a membership.'
          const userAllTickets =
            errors[0].message ===
            'You have already purchased the maximum number of membership tickets for your account.'
          if (notMember) {
            this.userMessageDialog = {
              btnText: 'Postani član',
              btnLink:
                'https://app.core-event.co/organization/futsal-dinamo-ab77/sport-memberships-store',
              title: 'Još nisi član?',
              description:
                'Trenutno je u tijeku prodaja ulaznica za članove, a regularna prodaja počet će 17.03.2024. Kako biste osigurali svoju ulaznicu, pozivamo vas da postanete član i iskoristite prednost rane kupnje. Više informacija i mogućnost kupnje članstva možete pronaći na sljedećem linku:',
              target: '_blank',
            }
            this.showUserMessageDialog = true

            return
          }
          if (userAllTickets) {
            this.userMessageDialog = {
              title: 'Obavijest',
              description:
                'Iskoristili ste kupnju svih ulaznica uključenih u vaše članstvo. Za dodatne ulaznice morat ćete pričekati otvaranje regularne prodaje.',
              btnText: 'Razumijem',
              btnLink: 'https://www.core-event.co',
              target: '_self',
            }
            this.showUserMessageDialog = true

            return
          }
        }
        this.errorMessage = 'Error in populating order data. Try again later.'
        this.snackbarOpen = true

        return
      }

      localStorage.removeItem('order')

      if (this.totalPriceNum() === 0) {
        return this.$router.push({
          name: 'successful-payment',
          params: { orderUUID: this.order.uuid, lang: this.getLanguageCodeFromQueryParams() },
        })
      }
      this.form.paymentMethod = paymentType
      const form = await createPurchaseSubmitFormWSPay(
        this.form,
        this.totalPrice(),
        this.order,
        i18next.language,
        this.shoppingCart,
        this.event.id,
      )
      document.body.appendChild(form)
      form.submit()
    },
    async submitAttendee() {
      await this.createAttendee({ attendee: this.form })
      this.showMessage = true
      this.$v.$reset()
      await this.$router.replace({ query: undefined })
    },
    async submitForm() {
      return submitGuard(this, this.submitAttendee)
    },
    selectedTickets(tickets) {
      this.shoppingCart = tickets
    },
    removeExistingLocation(seatInfo) {
      const [currTicket, seatLocation, _, oldTicket] = seatInfo
      const [section, row, seat] = seatLocation.split('_')
      const existingLocation = this.ticketLocations[oldTicket.id].find(
        location => location['section'] === section && location['row'] === row,
      )
      existingLocation.seats.splice(existingLocation.seats.indexOf(seat.split('-')[1]), 1)
      !existingLocation.seats.length &&
        this.ticketLocations[oldTicket.id].splice(
          this.ticketLocations[oldTicket.id].indexOf(existingLocation),
          1,
        )
      if (this.ticketLocations[oldTicket.id] && !this.ticketLocations[oldTicket.id].length) {
        delete this.ticketLocations[oldTicket.id]
      }
    },
    removeSelectedSeat(seatInfo) {
      const [currTicket, seatLocation, seatHasSameColorAsCurrTicket, oldTicket] = seatInfo
      this.removeExistingLocation(seatInfo)
      const ticketIndex = this.shoppingCart.findIndex(oi => oi.id === oldTicket.id)
      this.shoppingCart.splice(ticketIndex, 1)
      if (this.event.eventTemplate && !seatHasSameColorAsCurrTicket) {
        this.addSelectedSeat([currTicket, seatLocation])
      }
    },
    removeSelectedTableTickets(seatInfo) {
      const [ticket, seatLocation, seatHasSameColorAsCurrTicket, oldTicket, tableDetails] = seatInfo
      this.removeExistingLocation(seatInfo)
      const ticketIndex = this.shoppingCart.findIndex(oi => oi.id === ticket.id)
      for (let i = 0; i < tableDetails[seatLocation]; i++) {
        this.shoppingCart.splice(ticketIndex, 1)
      }
    },
    addSelectedSeat(seatInfo) {
      const [ticket, seatLocation] = seatInfo
      !!seatLocation && this.insertNewSeat(ticket, seatLocation)
      this.shoppingCart.push(ticket)
    },
    addDialogElementToOrder(ticketInfo) {
      const [ticket, quantity, type, location] = ticketInfo
      if (type === TicketType.TABLE) {
        this.insertNewSeat(ticket, location)
      } else {
        this.shoppingCart = this.shoppingCart.filter(t => t.id !== ticket.id)
      }
      for (let i = 0; i < quantity; i++) {
        this.shoppingCart.push(ticket)
      }
    },
    insertNewSeat(ticket, seatLocation) {
      const isTable = ticket.itemMeta && ticket.itemMeta.table
      const [section, row, seat] = seatLocation.split('_')
      if (!this.ticketLocations[ticket.id]) {
        return (this.ticketLocations[ticket.id] = [
          {
            section: section,
            row: row,
            seats: [seat.split('-')[1]],
            isTable,
          },
        ])
      }
      const existingLocation = this.ticketLocations[ticket.id].find(
        location => location['section'] === section && location['row'] === row,
      )
      if (existingLocation) {
        return (existingLocation.seats = [...existingLocation.seats, seat.split('-')[1]])
      }
      this.ticketLocations[ticket.id] = [
        ...this.ticketLocations[ticket.id],
        {
          section: section,
          row: row,
          seats: [seat.split('-')[1]],
          isTable,
        },
      ]
    },
    selectedSeat(seatInfo) {
      const [ticket, purchases] = seatInfo
      purchases.map(p => {
        !p.orderItem.itemMeta.parter &&
          this.insertNewSeat(p.orderItem, p.accreditation.ticketLocation)
      })
    },
    addAnother() {
      this.form = {}
      this.activeStep = 1
      this.showMessage = false
    },
    applyPromoCode(promoCodeInput) {
      this.promoCode = promoCodeInput
    },
    applyMembership(membership) {
      this.membership = membership
    },
    applySubscription(subscription) {
      this.subscription.push(subscription)
    },
    removeSubscription() {
      this.subscription = []
    },
    removePromoCode() {
      this.promoCode = null
    },
    totalPriceNum() {
      return this.$refs.ticketPriceList.calculateTotalPrice()
    },
    totalPrice() {
      return this.$refs.ticketPriceList.getTotalPrice()
    },
    goBack() {
      this.$router.push({
        name: 'event-public',
        params: { slug: this.event.slug },
      })
    },
    applyGiftCard(giftCardData) {
      this.giftCard = giftCardData
    },
    redirectToDefaultBrowser() {
      const currUrl = window.location.href
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        const iosVersion = getIOSVersion(navigator.userAgent)
        if (!iosVersion) {
          return
        }
        window.location.href = `${iosVersion < 17 ? 'com-apple-mobilesafari-tab:' : 'x-safari-'}${currUrl}`
      } else if (/Android/i.test(navigator.userAgent)) {
        window.location.href = `intent://${
          currUrl.replace(/^https?:\/\//, '')
        }#Intent;scheme=http${
          currUrl.includes('https://') ? 's' : ''
        };action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(currUrl)};end`
      }
    },
    removePromoCodeAndMembership() {
      this.handleRemoveMembershipZkm()
      this.removePromoCode()
    },
  },
}
</script>

<style scoped>
.container {
  min-height: 100%;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.card-header-container {
  margin-bottom: 48px;
}

.event-cover {
  background-size: cover;
  border-radius: 8px 8px 0 0;
  font-size: 0;
  display: flex;
}

@supports not (aspect-ratio: 16/9) {
  .event-cover {
    position: relative;
    height: 0;
    padding-top: 33.33333%;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.event-cover img {
  aspect-ratio: 16/9;
  object-fit: cover;
  width: 100%;
  border-radius: 8px 8px 0 0;
}

.cover-header-noTicket {
  margin: 150px 0 0 50px;
  height: auto;
}

.cover-header-style-zkm-border {
  border-bottom: none;
}

.my-page-title {
  font-family: Raleway, serif;
  font-style: normal;
  font-weight: bold;
  text-align: center;
  font-size: 38px;
  line-height: 45px;
  color: #ff4c00;
  margin-bottom: 24px;
  margin-top: 60px;
}

.description {
  font-family: Source Sans Pro, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 23px;
  text-align: center;
  color: #333333;
}

.card-header-container {
  margin-bottom: 48px;
}

.registration-success-page {
  max-width: 640px !important;
  align-items: center;
  margin: 120px 20px 0 20px;
  display: flex;
  flex-direction: column;
}

.info-image {
  width: 100%;
}

.ticket-list-container {
  margin-right: 30px;
}

.ticket-list-container-with-cover {
  @media screen and (min-width: 801px) and (max-width: 1000px) {
    margin-left: 1.5rem;
  }
}

/* layout */

.grid-container::before {
  content: '';
  grid-row: 1/-1;
}

.grid-container {
  max-width: 1440px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 366px) minmax(50%, 692px) minmax(25%, 366px);
  grid-template-rows: min-content min-content max-content;
}

/* .event-content-container {
  grid-column: 3;
  grid-row: 1;
  display: flex;
  flex-direction: column;
} */

.event-cover {
  grid-column: 3;
  grid-row: 1;
}

.overflow-card {
  grid-column: 3;
}

.cover-header-style {
  grid-column: 3;
}

main {
  grid-column: 2 / 3;
  grid-row: 1 / 10;
}

.main-no-image {
  grid-column: 2 / 3;
}

.main-no-image-zkm {
  grid-column: 1 / 3;
}

@media screen and (max-width: 992px) and (min-width: 801px) {
  .main-no-image {
    margin: 0 1.5rem;
  }
}

@media screen and (max-width: 992px) {
  .event-cover {
    display: none !important;
    grid-column: 1;
  }

  .register-layout {
    flex-direction: column-reverse;
  }

  .cover-header-noTicket {
    margin: 0;
  }

  .grid-container {
    grid-template-columns: 1fr;

    @media screen and (max-width: 1000px) {
      display: unset;
    }
  }

  .overflow-card {
    grid-column: 1;
    grid-row: 1;
  }

  main {
    grid-column: 1;
    grid-row: 2;
  }

  .main-no-image {
    grid-column: 1;
    grid-row: 2;
  }

  .cover-header-style {
    margin: 0;
    grid-column: 1;
  }

  .ticket-list-container {
    margin-right: 0;
  }
}

@media screen and (max-width: 375px) {
  .cover-header-style-zkm {
    width: unset;
    margin: 0 auto;
  }
}

.no-tickets {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3rem;
}

.no-tickets img {
  margin: 64px 0;
}

.checkout-timer-regular {
  margin-right: 1.5rem;
}

@media screen and (max-width: 992px) {
  .checkout-timer-regular {
    display: none;
  }
}

@media screen and (max-width: 1000px) {
  .checkout-timer-regular-mobile,
  .checkout-timer-regular-mobile-no-cover {
    margin: -1rem -1.5rem 0 -1.5rem;
    position: fixed;
    width: 100%;
    border-left: unset;
    border-right: unset;
    border-radius: unset;
    z-index: 1;
  }
}

.checkout-timer-regular-mobile {
  @media screen and (min-width: 993px) {
    display: none;
  }
  @media screen and (max-width: 425px) {
    margin-top: -11.5rem;
    top: 231px;
  }
  @media screen and (max-width: 800px) and (min-width: 426px) {
    margin-top: -10.75rem;
    top: 252px;
  }
  @media screen and (max-width: 992px) and (min-width: 801px) {
    margin-top: -10.5rem;
    top: 248px;
  }
}

.checkout-timer-regular-mobile-no-cover {
  @media screen and (min-width: 993px) {
    display: none;
  }
  @media screen and (max-width: 425px) {
    margin-top: -11.5rem;
    top: 231px;
  }
  @media screen and (max-width: 800px) and (min-width: 426px) {
    margin-top: -10.75rem;
    top: 252px;
  }
  @media screen and (max-width: 992px) and (min-width: 801px) {
    margin-top: -12.25rem;
    top: 276px;
  }
}

.promo-code-container {
  padding-top: 1.5rem;
  border-radius: 0.25rem;
  border: 1px solid var(--stroke-one, rgba(151, 151, 151, 0.5));
  margin-top: 1.5rem;
  @media screen and (max-width: 800px) and (min-width: 600px) {
    margin-left: -1.5rem;
    margin-right: -1.5rem;
  }
  @media screen and (max-width: 1000px) {
    border: unset;
  }
}

.event-cover,
.overflow-card-step-two {
  @media screen and (max-width: 992px) {
    display: none;
  }
}

.card-header-with-cover {
  @media screen and (min-width: 993px) {
    margin-right: 1.5rem;
  }
}

.open-in-default-browser-container {
  display: flex;
  padding: 2rem 1rem 1rem 1rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
}

.external-browser-text {
  margin: 0;
  color: #666;
  text-align: center;
  font-family: "Source Sans Pro";
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.email-login-divider {
  display: flex;
  align-items: center;
  max-width: 500px;
  align-self: center;
  width: 100%;
  margin-top: 3rem;
}

.spacer {
  flex: 1;
  border: 0.5px solid rgba(151, 151, 151, 0.5);
}

.separator-text {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 150%;
  text-align: center;
  color: #333333;
  margin-right: 1rem;
  margin-left: 1rem;
}

.google-login-disabled-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  flex: 1 0 0;
  align-self: stretch;
}

.continue-here-btn {
  width: 100%;
}

::v-deep .continue-here-btn .v-btn__content {
  justify-content: space-between;
}
</style>
